import { arbitrum, mainnet } from "viem/chains";
import type { Chain } from "viem";

/**
 * Kelp's rsETH LayerZero v2 OFT route, Ethereum <-> Arbitrum.
 *
 * Every other chain bridges over Chainlink CCIP through Transporter (see
 * `@constants/bridgeRoutes`). Arbitrum is the one chain still on LayerZero,
 * and LayerZero removed rsETH from their own UI, so without this there is no
 * path for Arbitrum holders at all.
 *
 * This is expected to be temporary. When Arbitrum migrates to CCIP, delete
 * this module and add Arbitrum to the Transporter route table instead.
 *
 * Addresses verified on-chain: the Arbitrum OFT and the Ethereum adapter peer
 * to each other, and the adapter's locked balance tracks Arbitrum's supply.
 * The adapter appears in no other config and lives only here; the Arbitrum
 * token must stay in sync with `contractConfig[arbitrum.id].xtoken`.
 */

export const OFT_LOCAL_DECIMALS = 18;
export const OFT_SHARED_DECIMALS = 6;

/**
 * Amounts are floored to this multiple before quoting and sending. The OFT
 * silently drops anything finer, so rounding up front keeps the quoted amount
 * and the sent amount identical.
 */
export const OFT_DUST_GRANULARITY =
  10n ** BigInt(OFT_LOCAL_DECIMALS - OFT_SHARED_DECIMALS);

export type OftChainEntry = {
  chain: Chain;
  /** LayerZero v2 endpoint id. */
  eid: number;
  /** Contract exposing `quoteSend` / `send`. */
  oft: `0x${string}`;
  /** The rsETH ERC-20 the user holds. Identical to `oft` on Arbitrum. */
  token: `0x${string}`;
  /** Only Ethereum's adapter pulls the ERC-20, so only it needs an approval. */
  approvalRequired: boolean;
};

export const OFT_HUB: OftChainEntry = {
  chain: mainnet,
  eid: 30101,
  oft: "0x85d456b2dff1fd8245387c0bfb64dfb700e98ef3",
  token: "0xA1290d69c65A6Fe4DF752f95823fae25cB99e5A7",
  approvalRequired: true,
};

export const OFT_SPOKES: OftChainEntry[] = [
  {
    chain: arbitrum,
    eid: 30110,
    oft: "0x4186BFC76E2E237523CBC30FD220FE055156b41F",
    token: "0x4186BFC76E2E237523CBC30FD220FE055156b41F",
    approvalRequired: false,
  },
];

export const oftBridgePolicy = {
  quoteRefetchMs: 30_000,
  allowanceRefetchMs: 15_000,
  /** Cadence for live wallet/token balance polling on the bridge and wrap cards. */
  balanceRefetchMs: 10_000,
  layerZeroScanTxUrl: (hash: string) => `https://layerzeroscan.com/tx/${hash}`,
} as const;
